import React from 'react';
import {alpha} from '@mui/material/styles';

import {blue, red, warningOrange} from '@embertracking/ui/style/themePrimitives';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';

// The one banner every page-top notice renders through — operator session,
// station session, unverified email, missing second factor. They used to be
// three hand-rolled boxes and a MUI Alert, each with its own padding, icon
// size and button treatment, and stacked together they read as four different
// components. Same shell, three tones:
//
//   danger   the operator session — a privileged session, not a limited one
//   warning  something the member should fix (email, MFA)
//   info     context about the session (a shared station login)
//
// `warning` is the theme's warning orange (warningOrange in themePrimitives),
// with its readable dark-orange text — orange itself is too light to read.
const TONES = {
    danger: {bg: red[50], border: red[100], icon: red[500], text: red[800], button: red[200], buttonHover: red[300]},
    warning: {bg: warningOrange.surface, border: warningOrange.border, icon: warningOrange.icon, text: warningOrange.text, button: warningOrange.tint, buttonHover: warningOrange.border},
    info: {bg: blue[50], border: blue[100], icon: blue[500], text: blue[800], button: blue[200], buttonHover: blue[300]}
};

// icon      — an @mui/icons-material element; sized and coloured here
// action    — {label, onClick, testId}: the one primary button on the right
// onDismiss — when set, a close control after the action (a banner that says
//             what the session IS shouldn't offer one; a nudge may)
export default function AppBanner({tone = 'info', icon, children, action, onDismiss, testId, sx}) {
    const colors = TONES[tone] || TONES.info;

    return (
        <Box
            sx={{
                bgcolor: colors.bg,
                // iOS Safari renders the page up behind the status bar, so the
                // banner needs a defined top edge there. Gate on the iOS-only
                // `-webkit-touch-callout` support so no other platform is affected.
                '@supports (-webkit-touch-callout: none)': {
                    borderTop: `1px solid ${colors.border}`
                },
                borderBottom: `1px solid ${colors.border}`,
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                px: 2,
                py: 1.25,
                flexWrap: 'wrap',
                ...sx
            }}
            data-testid={testId}
        >
            <Stack direction="row" spacing={1} sx={{alignItems: 'center', flex: 1, minWidth: 0}}>
                {icon && React.cloneElement(icon, {sx: {color: colors.icon, fontSize: 20, flexShrink: 0, ...icon.props.sx}})}
                <Typography variant="body2" sx={{color: colors.text}}>
                    {children}
                </Typography>
            </Stack>
            {action && (
                <Button
                    size="small"
                    variant="contained"
                    color="inherit"
                    onClick={action.onClick}
                    data-testid={action.testId}
                    sx={{
                        backgroundColor: alpha(colors.button, 0.5),
                        color: alpha('#000', 0.85),
                        fontWeight: 'bold',
                        boxShadow: 'none',
                        whiteSpace: 'nowrap',
                        '&:hover': {
                            backgroundColor: alpha(colors.buttonHover, 0.5),
                            boxShadow: 'none'
                        }
                    }}
                >
                    {action.label}
                </Button>
            )}
            {onDismiss && (
                <IconButton
                    size="small"
                    aria-label="Dismiss"
                    onClick={onDismiss}
                    data-testid={testId ? `${testId}-dismiss` : undefined}
                    sx={{color: colors.text, ml: -0.5}}
                >
                    <CloseIcon fontSize="small" />
                </IconButton>
            )}
        </Box>
    );
}
